const generation = {
    system: 'OpenAI Codex AI assistant',
    date: '14 August 2026',
    status: 'Human review pending',
    note: 'The AI synthesized the draft from the listed biblical and scholarly sources. Associations describe broad affinities, not formal endorsements by every person or tradition represented.',
};

export const additionalPerspectives = [
    {
        slug: 'what-does-paul-mean-by-justification',
        category: 'Paul and Salvation',
        question: 'What does Paul mean by justification?',
        shortTitle: 'What Paul means by justification',
        summary: 'Paul uses the language of justification to describe God setting people right through Christ. The debate concerns whether its centre is a forensic verdict, recognition of covenant membership, transformation through participation in Christ, or a combination of these dimensions.',
        introduction: 'The main readings agree that justification is God’s gracious act in Christ and not a human achievement. They differ over which biblical image governs the others: the law court, Abraham’s covenant family, or incorporation into Christ and the Spirit-shaped life that follows.',
        keyTexts: [
            { label: 'Romans 3:21–31', href: '/read/Rom.3/#Rom.3.21-Rom.3.31' },
            { label: 'Romans 4:1–25', href: '/read/Rom.4/' },
            { label: 'Romans 5:1–11', href: '/read/Rom.5/#Rom.5.1-Rom.5.11' },
            { label: 'Galatians 2:15–21', href: '/read/Gal.2/#Gal.2.15-Gal.2.21' },
        ],
        readings: [
            {
                id: 'forensic-verdict',
                title: 'God declares believers righteous through faith',
                thesis: 'God declares sinners righteous because of Christ, and faith receives rather than earns that verdict.',
                accent: 'navy',
                badges: ['Traditional Protestant', 'Conservative', 'Lutheran', 'Reformed'],
                summary: 'This reading treats justification primarily as law-court language. God’s verdict changes a person’s standing before him: the ungodly are counted righteous through faith in Christ rather than through works. Sanctification and new obedience necessarily follow, but they are distinguished from the ground of the justifying verdict.',
                contribution: 'It protects the priority of grace and the sufficiency of Christ by locating assurance outside the believer’s moral achievement. It also makes sense of Paul’s repeated contrasts between faith, boasting, works, and gift.',
                caution: 'If isolated from Paul’s covenant and participation language, this account can make justification sound individualistic or detached from the creation of a reconciled Jew–Gentile people.',
                supporters: [
                    { name: 'Martin Luther', sourceId: 'luther-galatians' },
                    { name: 'John Calvin', sourceId: 'calvin-romans' },
                ],
                predicates: [
                    {
                        label: '“Works of the Law” cannot provide the basis for God’s verdict',
                        topicSlug: 'what-does-paul-mean-by-works-of-the-law',
                        readingId: 'human-achievement',
                        linked: true,
                    },
                    {
                        label: 'Christ’s righteousness is the decisive ground of acceptance',
                        topicSlug: 'is-christs-righteousness-counted-or-imparted',
                        readingId: 'counted-verdict',
                        linked: true,
                    },
                ],
                relations: [
                    { type: 'contrasts with', target: 'covenant-membership', linked: true },
                    { type: 'complements', target: 'transformative-participation', linked: true },
                ],
            },
            {
                id: 'covenant-membership',
                title: 'Justification declares who belongs to God’s people',
                thesis: 'Justification declares who belongs to Abraham’s one family now and anticipates God’s final verdict.',
                accent: 'teal',
                badges: ['New Perspective', 'Progressive', 'Ecumenical'],
                summary: 'This approach keeps the law-court metaphor but places it inside Paul’s covenant story. God’s verdict identifies the people who belong to the family promised to Abraham, now marked out by faith in Christ rather than circumcision or ethnic Torah badges. The present declaration anticipates the final judgment in which God publicly vindicates his people.',
                contribution: 'It connects justification to ecclesiology, mission, and Jew–Gentile reconciliation. Romans and Galatians become arguments not only about how an individual is saved, but also about who may share the same table as a full member of God’s people.',
                caution: 'Critics argue that covenant membership describes an effect of justification without fully explaining how guilty people receive a righteous verdict before God.',
                supporters: [
                    { name: 'N. T. Wright', sourceId: 'wright-justification' },
                    { name: 'James D. G. Dunn', sourceId: 'dunn-new-perspective' },
                ],
                predicates: [
                    {
                        label: 'Jew–Gentile table fellowship displays the gospel’s verdict',
                        topicSlug: 'why-did-table-fellowship-matter',
                        readingId: 'gospel-enacted',
                        linked: true,
                    },
                    {
                        label: 'Boundary-marking works cannot define Abraham’s family',
                        topicSlug: 'what-does-paul-mean-by-works-of-the-law',
                        readingId: 'boundary-markers',
                        linked: true,
                    },
                ],
                relations: [
                    { type: 'qualifies', target: 'forensic-verdict', linked: true },
                    { type: 'overlaps with', target: 'transformative-participation', linked: true },
                ],
            },
            {
                id: 'transformative-participation',
                title: 'Righteousness transforms life in Christ',
                thesis: 'God sets people right by joining them to the faithful, crucified, and risen Messiah.',
                accent: 'amber',
                badges: ['Catholic', 'Orthodox', 'Participationist', 'Ecumenical'],
                summary: 'This family of readings emphasizes union with Christ and the gift of the Spirit. Justification is not reduced to moral improvement, yet God’s saving verdict and the believer’s new life belong to one reality: participation in Christ’s death and resurrection. Different Catholic, Orthodox, and Protestant accounts explain that relationship in distinct ways.',
                contribution: 'It takes seriously Paul’s language of being “in Christ,” dying and rising with him, and receiving the Spirit. It also helps explain why Paul can speak of salvation as both a gift already given and a life that transforms communities.',
                caution: 'The category can become too broad unless it clearly distinguishes God’s initiating grace, the basis of justification, and the transformed life that results.',
                supporters: [
                    { name: 'Michael J. Gorman', sourceId: 'gorman-justification' },
                    { name: 'Catholic–Lutheran Joint Declaration', sourceId: 'joint-declaration' },
                ],
                predicates: [
                    {
                        label: 'Union with Christ is a controlling centre of Paul’s salvation language',
                        topicSlug: 'how-does-union-with-christ-relate-to-justification',
                        readingId: 'union-as-foundation',
                        linked: true,
                    },
                    {
                        label: 'The new covenant gives the Spirit who produces faithful obedience',
                        topicSlug: 'how-does-the-old-covenant-relate-to-the-new',
                        readingId: 'fulfilled-and-transformed',
                        linked: true,
                    },
                ],
                relations: [
                    { type: 'integrates', target: 'forensic-verdict', linked: true },
                    { type: 'broadens', target: 'covenant-membership', linked: true },
                ],
            },
        ],
        sources: [
            {
                id: 'luther-galatians',
                author: 'Martin Luther',
                title: 'Commentary on St. Paul’s Epistle to the Galatians',
                detail: 'A classic Protestant account of justification by faith apart from works.',
                href: 'https://www.ccel.org/ccel/luther/galatians.toc.html',
            },
            {
                id: 'calvin-romans',
                author: 'John Calvin',
                title: 'Commentary on Romans',
                detail: 'A classic Reformed treatment of Paul’s law-court and grace language.',
                href: 'https://ccel.org/ccel/calvin/calcom38/calcom38',
            },
            {
                id: 'wright-justification',
                author: 'N. T. Wright',
                title: 'Justification: God’s Plan and Paul’s Vision',
                detail: 'A defense of justification as law-court language situated within covenant and eschatology.',
                href: 'https://www.ivpress.com/justification',
            },
            {
                id: 'dunn-new-perspective',
                author: 'James D. G. Dunn',
                title: 'The New Perspective on Paul',
                detail: 'Collected essays connecting justification, covenant identity, and Gentile inclusion.',
                href: 'https://www.eerdmans.com/9780802845627/the-new-perspective-on-paul/',
            },
            {
                id: 'gorman-justification',
                author: 'Michael J. Gorman',
                title: 'Wright about Much, but Questions about Justification',
                detail: 'A scholarly engagement that presses justification toward participation and transformation in Christ.',
                href: 'https://scholarlypublishingcollective.org/psup/study-of-paul/article/4/1/27/255632/Wright-about-Much-but-Questions-about',
            },
            {
                id: 'joint-declaration',
                author: 'Catholic Church and Lutheran World Federation',
                title: 'Joint Declaration on the Doctrine of Justification',
                detail: 'An ecumenical statement recording agreement on basic truths while acknowledging remaining differences.',
                href: 'https://www.vatican.va/content/john-paul-ii/en/angelus/1999/documents/hf_jp-ii_ang_31101999.html',
            },
        ],
        generation,
    },
    {
        slug: 'why-did-table-fellowship-matter',
        category: 'Paul and Community',
        question: 'Why did table fellowship matter in Paul’s churches?',
        shortTitle: 'Why table fellowship mattered',
        summary: 'Shared meals made belonging visible in the ancient church. Paul’s confrontation with Peter at Antioch therefore concerns more than etiquette: it tests whether Jewish and Gentile believers can inhabit one community without requiring Gentiles to become Jews.',
        introduction: 'Interpreters differ over exactly what Peter feared and what practices were at stake, but they agree that withdrawing from a shared table communicated a theological judgment. The meal embodied boundaries of purity, identity, status, and gospel fellowship.',
        keyTexts: [
            { label: 'Galatians 2:11–21', href: '/read/Gal.2/#Gal.2.11-Gal.2.21' },
            { label: 'Acts 10:1–11:18', href: '/read/Acts.10/' },
            { label: 'Romans 14:1–15:7', href: '/read/Rom.14/' },
            { label: '1 Corinthians 8–10', href: '/read/1Cor.8/' },
        ],
        readings: [
            {
                id: 'purity-and-identity',
                title: 'Purity practices shaped community identity',
                thesis: 'Meals expressed Jewish communal identity, and withdrawal protected boundaries some believers considered religiously necessary.',
                accent: 'navy',
                badges: ['Historical', 'Jewish-contextual', 'Second Temple'],
                summary: 'This reading begins with the social world of Jewish food practice. Table fellowship could signal whose food was trustworthy, whose home could be entered, and where communal boundaries lay. Peter’s withdrawal may therefore reflect pressure to preserve recognizably Jewish practice rather than simple personal hostility toward Gentiles.',
                contribution: 'It prevents the Antioch incident from becoming a modern story about private prejudice alone. Paul’s sharp response is better understood when the real religious and social costs borne by Jewish Christ-followers are kept in view.',
                caution: 'Historical reconstruction cannot by itself explain why Paul calls the withdrawal a failure to walk in line with the truth of the gospel.',
                supporters: [
                    { name: 'Philip F. Esler', sourceId: 'esler-table' },
                    { name: 'Mark D. Nanos', sourceId: 'nanos-antioch' },
                ],
                predicates: [
                    {
                        label: 'Meals communicate communal and religious belonging',
                        topicSlug: 'what-did-shared-meals-communicate',
                        readingId: 'meals-as-boundaries',
                        linked: true,
                    },
                    {
                        label: 'Paul’s Jewish practice should not be assumed to have ended',
                        topicSlug: 'did-paul-remain-torah-observant',
                        readingId: 'torah-observant-jew',
                        linked: true,
                    },
                ],
                relations: [
                    { type: 'sets context for', target: 'gospel-enacted', linked: true },
                    { type: 'qualifies', target: 'conversion-pressure', linked: true },
                ],
            },
            {
                id: 'gospel-enacted',
                title: 'Shared meals made the gospel visible',
                thesis: 'Eating together displayed God’s equal welcome of Jews and Gentiles; separation denied that message in practice.',
                accent: 'teal',
                badges: ['New Perspective', 'Progressive', 'Ecumenical'],
                summary: 'On this account, the shared table made the content of justification visible. If Gentiles belong to Abraham’s family through faith in Christ, withdrawing from them implies that faith is insufficient for full fellowship. Paul sees the practice as rebuilding the very division the gospel has overcome.',
                contribution: 'This reading joins doctrine and community ethics. Justification is not merely a private status before God; it creates a reconciled people whose meals, welcome, and patterns of belonging must correspond to God’s verdict.',
                caution: 'The theological force of the meal should not erase ongoing disagreements about kosher practice or the distinct callings of Jewish and Gentile believers.',
                supporters: [
                    { name: 'James D. G. Dunn', sourceId: 'dunn-new-perspective' },
                    { name: 'N. T. Wright', sourceId: 'wright-justification' },
                ],
                predicates: [
                    {
                        label: 'Justification identifies a single covenant family',
                        topicSlug: 'what-does-paul-mean-by-justification',
                        readingId: 'covenant-membership',
                        linked: true,
                    },
                    {
                        label: 'Boundary markers cannot be conditions of Gentile belonging',
                        topicSlug: 'what-does-paul-mean-by-works-of-the-law',
                        readingId: 'boundary-markers',
                        linked: true,
                    },
                ],
                relations: [
                    { type: 'interprets', target: 'purity-and-identity', linked: true },
                    { type: 'resists', target: 'conversion-pressure', linked: true },
                ],
            },
            {
                id: 'conversion-pressure',
                title: 'Gentiles were pressured to become Jews',
                thesis: 'Separate tables pressured Gentiles to Judaize in order to gain unrestricted fellowship.',
                accent: 'amber',
                badges: ['Paul within Judaism', 'Progressive', 'Jewish-contextual'],
                summary: 'This view focuses on the social effect of withdrawal. Gentile believers could escape marginal status by adopting circumcision and a Jewish pattern of Torah observance. Paul objects because God is calling non-Jews as non-Jews through Israel’s Messiah, not because Jewish practice itself is defective.',
                contribution: 'It explains why table fellowship, circumcision, and Paul’s Gentile mission appear together. It also distinguishes opposition to compulsory Gentile Judaizing from opposition to continued Jewish Torah observance.',
                caution: 'Critics ask whether Paul’s statements about justification and the Law can be limited to Gentile status or whether they make wider claims about all humanity.',
                supporters: [
                    { name: 'Mark D. Nanos', sourceId: 'nanos-antioch' },
                    { name: 'Magnus Zetterholm', sourceId: 'paul-within-judaism' },
                ],
                predicates: [
                    {
                        label: 'Gentiles should not be required to become Jews',
                        topicSlug: 'what-does-paul-mean-by-works-of-the-law',
                        readingId: 'gentile-assimilation',
                        linked: true,
                    },
                    {
                        label: 'Jewish and Gentile identities can remain meaningful in Christ',
                        topicSlug: 'does-the-gospel-erase-jewish-and-gentile-identity',
                        readingId: 'distinct-within-unity',
                        linked: true,
                    },
                ],
                relations: [
                    { type: 'develops', target: 'purity-and-identity', linked: true },
                    { type: 'overlaps with', target: 'gospel-enacted', linked: true },
                ],
            },
        ],
        sources: [
            {
                id: 'esler-table',
                author: 'Philip F. Esler',
                title: 'Shared meals shaped community and mission',
                detail: 'A social-historical study of Jewish–Gentile table fellowship in early Christian identity.',
                href: 'https://www.cambridge.org/core/services/aop-cambridge-core/content/view/827E480FD679099244033220489ED862/9780511554933c4_p71-109_CBO.pdf/tablefellowship.pdf',
            },
            {
                id: 'nanos-antioch',
                author: 'Mark D. Nanos',
                title: 'The Antioch dispute concerned Gentile inclusion',
                detail: 'Representative work interpreting Galatians from within a Jewish communal setting.',
                href: 'https://ejournals.bc.edu/index.php/scjr/article/view/1511',
            },
            {
                id: 'dunn-new-perspective',
                author: 'James D. G. Dunn',
                title: 'The New Perspective on Paul',
                detail: 'Essays emphasizing the connection between works of the Law and Jewish–Gentile fellowship.',
                href: 'https://www.eerdmans.com/9780802845627/the-new-perspective-on-paul/',
            },
            {
                id: 'wright-justification',
                author: 'N. T. Wright',
                title: 'Justification: God’s Plan and Paul’s Vision',
                detail: 'An account connecting God’s verdict with the identity of the covenant family.',
                href: 'https://www.ivpress.com/justification',
            },
            {
                id: 'paul-within-judaism',
                author: 'Mark D. Nanos and Magnus Zetterholm, editors',
                title: 'Paul Within Judaism',
                detail: 'Essays distinguishing Paul’s Gentile mission from a rejection of Jewish Torah practice.',
                href: 'https://www.jstor.org/stable/j.ctt9m0vn7',
            },
            {
                id: 'gentile-mission',
                author: 'Philip F. Esler',
                title: 'Paul’s Gentile Mission and the Jewish Christian Community',
                detail: 'A scholarly study of fellowship and Gentile inclusion in the narrative of Galatians 1–2.',
                href: 'https://www.cambridge.org/core/journals/new-testament-studies/article/pauls-gentile-mission-and-the-jewish-christian-communitya-study-of-the-narrative-in-galatians-1-and-2/04B2EB3E684467FE48498675D512985F',
            },
        ],
        generation,
    },
    {
        slug: 'how-does-the-old-covenant-relate-to-the-new',
        category: 'Biblical Theology',
        question: 'How does the old covenant relate to the new?',
        shortTitle: 'How the old and new covenants relate',
        summary: 'Christians agree that the new covenant fulfils God’s promises in Christ, but differ over the degree of continuity between Israel and the church, the continuing role of Mosaic law, and the future of promises given to ethnic Israel.',
        introduction: 'This debate is not simply a choice between continuity and discontinuity. Each reading preserves both in a different pattern: one covenant of grace under different administrations, distinct dispensations within one divine plan, or progressive covenants that reach their fulfilment and transformation in Christ.',
        keyTexts: [
            { label: 'Jeremiah 31:31–34', href: '/read/Jer.31/#Jer.31.31-Jer.31.34' },
            { label: '2 Corinthians 3:4–18', href: '/read/2Cor.3/#2Cor.3.4-2Cor.3.18' },
            { label: 'Hebrews 8:6–13', href: '/read/Heb.8/#Heb.8.6-Heb.8.13' },
            { label: 'Romans 9–11', href: '/read/Rom.9/' },
        ],
        readings: [
            {
                id: 'one-covenant-of-grace',
                title: 'God saves through one covenant of grace',
                thesis: 'Old and new testaments are distinct administrations of the one gracious promise fulfilled in Christ.',
                accent: 'navy',
                badges: ['Covenant Theology', 'Conservative', 'Reformed'],
                summary: 'Classic Reformed covenant theology sees substantial unity in God’s saving purpose. Promises, sacrifices, circumcision, and other old-covenant ordinances anticipated Christ; the new covenant administers the same grace with greater clarity and fullness. Ceremonial and civil laws expire with their redemptive-historical purpose, while the moral law continues.',
                contribution: 'This reading strongly unifies Scripture and the people of God while preserving development from promise to fulfilment. It explains why the New Testament can appropriate Israel’s Scriptures directly for the church.',
                caution: 'Critics argue that the model can understate the newness of Jeremiah 31 or too quickly transfer promises made specifically to Israel to the church.',
                supporters: [
                    { name: 'Westminster Assembly', sourceId: 'westminster-confession' },
                    { name: 'Orthodox Presbyterian Church', sourceId: 'opc-covenant' },
                ],
                predicates: [
                    {
                        label: 'God has one saving purpose centred in Christ across both testaments',
                        topicSlug: 'is-there-one-saving-purpose-across-the-covenants',
                        readingId: 'one-gracious-covenant',
                        linked: true,
                    },
                    {
                        label: 'The Mosaic Law cannot justify but still witnesses to God’s moral will',
                        topicSlug: 'what-does-paul-mean-by-works-of-the-law',
                        readingId: 'whole-torah',
                        linked: true,
                    },
                ],
                relations: [
                    { type: 'contrasts with', target: 'distinct-dispensations', linked: true },
                    { type: 'overlaps with', target: 'fulfilled-and-transformed', linked: true },
                ],
            },
            {
                id: 'distinct-dispensations',
                title: 'God’s promises to Israel remain distinct',
                thesis: 'The church participates in new-covenant blessing without cancelling promises made specifically to national Israel.',
                accent: 'red',
                badges: ['Dispensational', 'Conservative', 'Evangelical'],
                summary: 'Dispensational readings emphasize grammatical-historical interpretation and distinctions between Israel and the church. The Mosaic covenant is no longer the rule governing the church, yet unconditional biblical covenants preserve a future for Israel. Dispensationalists differ over precisely how the church relates to the new covenant.',
                contribution: 'It keeps the original addressees of Old Testament promises visible and resists making the church a simple replacement for Israel. It also gives substantial weight to Paul’s expectation for Israel in Romans 9–11.',
                caution: 'Critics question whether separating Israel and the church adequately reflects the New Testament’s language about one people in Christ and shared participation in Abrahamic promises.',
                supporters: [
                    { name: 'Council on Dispensational Hermeneutics', sourceId: 'dispensational-covenant' },
                    { name: 'Robert Gromacki', sourceId: 'gromacki-dispensations' },
                ],
                predicates: [
                    {
                        label: 'Unconditional promises to Israel retain their original national reference',
                        topicSlug: 'does-god-still-have-a-future-for-israel',
                        readingId: 'distinct-future',
                        linked: true,
                    },
                    {
                        label: 'Israel and the church remain distinguishable in God’s plan',
                        topicSlug: 'are-israel-and-the-church-distinct-in-gods-plan',
                        readingId: 'distinct-peoples',
                        linked: true,
                    },
                ],
                relations: [
                    { type: 'contrasts with', target: 'one-covenant-of-grace', linked: true },
                    { type: 'qualifies', target: 'fulfilled-and-transformed', linked: true },
                ],
            },
            {
                id: 'fulfilled-and-transformed',
                title: 'Christ fulfils and transforms earlier covenants',
                thesis: 'The covenants progressively unfold until Christ fulfils their types and establishes the new-covenant people.',
                accent: 'amber',
                badges: ['Progressive Covenantal', 'Conservative', 'Evangelical'],
                summary: 'This proposal treats the biblical covenants as the backbone of Scripture’s developing story. It does not place all covenants under one theological covenant in the classic Reformed manner, nor maintain the same Israel–church distinction as dispensationalism. Christ fulfils the earlier covenants, and their promises reach the church through union with him.',
                contribution: 'It emphasizes both progression and fulfilment, offering a mediating account of how law, land, temple, kingship, and peoplehood are transformed around Christ and the new covenant.',
                caution: 'Critics from both neighbouring systems contend that the model either preserves too little continuity with Israel or too little distinction between the covenants.',
                supporters: [
                    { name: 'Peter J. Gentry', sourceId: 'kingdom-through-covenant' },
                    { name: 'Stephen J. Wellum', sourceId: 'kingdom-through-covenant' },
                ],
                predicates: [
                    {
                        label: 'Biblical covenants progressively reveal one plan centred in Christ',
                        topicSlug: 'how-do-biblical-covenants-unfold-around-christ',
                        readingId: 'progressive-map',
                        linked: true,
                    },
                    {
                        label: 'The Mosaic covenant cannot govern the new-covenant people unchanged',
                        topicSlug: 'what-does-paul-mean-by-works-of-the-law',
                        readingId: 'whole-torah',
                        linked: true,
                    },
                ],
                relations: [
                    { type: 'mediates between', target: 'one-covenant-of-grace', linked: true },
                    { type: 'mediates between', target: 'distinct-dispensations', linked: true },
                ],
            },
        ],
        sources: [
            {
                id: 'westminster-confession',
                author: 'Westminster Assembly',
                title: 'Westminster Confession of Faith, chapters 7 and 19',
                detail: 'The confessional account of one covenant of grace under old- and new-testament administrations and of the uses of the Law.',
                href: 'https://opc.org/WCF-WIP.html',
            },
            {
                id: 'opc-covenant',
                author: 'Orthodox Presbyterian Church',
                title: 'The Covenant of Grace',
                detail: 'An accessible Reformed explanation of covenant continuity, Christ, and the moral law.',
                href: 'https://www.opc.org/cce/covenant.html',
            },
            {
                id: 'dispensational-covenant',
                author: 'Council on Dispensational Hermeneutics',
                title: 'Dispensationalism and the New Covenant',
                detail: 'A collection showing multiple dispensational approaches to the church’s participation in the new covenant.',
                href: 'https://dispensationalcouncil.org/council-presentations-2009/',
            },
            {
                id: 'gromacki-dispensations',
                author: 'Robert Gromacki',
                title: 'Distinctives of Dispensational Preaching',
                detail: 'A traditional dispensational account of the dispensations and unconditional covenants for Israel.',
                href: 'https://dispensationalcouncil.org/wp-content/uploads/2018/07/12_Gromacki_Distinctives_of_Dispensational_Preaching.pdf',
            },
            {
                id: 'kingdom-through-covenant',
                author: 'Peter J. Gentry and Stephen J. Wellum',
                title: 'Kingdom through Covenant',
                detail: 'A progressive-covenantal account presented as an alternative to both covenant theology and dispensationalism.',
                href: 'https://www.crossway.org/books/kingdom-through-covenant-case-2/',
            },
        ],
        generation,
    },
    {
        slug: 'did-paul-remain-torah-observant',
        category: 'Paul and Jewish Identity',
        question: 'Did Paul remain Torah-observant?',
        shortTitle: 'Whether Paul remained Torah-observant',
        summary: 'Paul describes himself as a Jew, adapts his practice for mission, and also says that he died to the Law and is not under it. Interpreters disagree whether he continued Torah observance as a Jewish vocation, practiced it selectively, or regarded the Mosaic covenant as no longer binding after Christ.',
        introduction: 'The evidence comes from both Paul’s letters and Acts, and the sources do not answer every modern question about daily practice. The disagreement therefore turns on how to relate Paul’s Jewish identity, missionary flexibility, negative statements about the Law, and Luke’s accounts of his conduct.',
        keyTexts: [
            { label: 'Acts 21:17–26', href: '/read/Acts.21/#Acts.21.17-Acts.21.26' },
            { label: '1 Corinthians 7:17–24', href: '/read/1Cor.7/#1Cor.7.17-1Cor.7.24' },
            { label: '1 Corinthians 9:19–23', href: '/read/1Cor.9/#1Cor.9.19-1Cor.9.23' },
            { label: 'Galatians 2:15–21', href: '/read/Gal.2/#Gal.2.15-Gal.2.21' },
        ],
        readings: [
            {
                id: 'torah-observant-jew',
                title: 'Paul continued to live as a Torah-observant Jew',
                thesis: 'Paul opposed Gentile Judaizing while continuing Jewish Torah practice as part of his own calling.',
                accent: 'teal',
                badges: ['Paul within Judaism', 'Progressive', 'Jewish-contextual'],
                summary: 'This view distinguishes what Paul says to non-Jews from how Jewish Christ-followers should live. His rule that each remain in the calling in which they were called, together with Acts’ portrayal of his Jewish practice, suggests that Paul expected Jews—including himself—to continue Torah observance while denying that Torah could justify or give resurrection life.',
                contribution: 'It resists treating Paul’s mission as a conversion away from Judaism and sharply limits anti-Jewish uses of his letters. His gospel brings Gentiles to Israel’s God without erasing Jewish covenantal identity.',
                caution: 'Critics point to Paul’s statements about dying to the Law, not being under the Law, and treating practices flexibly, asking whether “Torah-observant” overstates the consistency of his conduct.',
                supporters: [
                    { name: 'Paul T. Sloan', sourceId: 'sloan-law-observance' },
                    { name: 'Magnus Zetterholm', sourceId: 'paul-within-judaism' },
                ],
                predicates: [
                    {
                        label: 'Paul’s prohibitions address Gentile Judaizing rather than Jewish practice',
                        topicSlug: 'what-does-paul-mean-by-works-of-the-law',
                        readingId: 'gentile-assimilation',
                        linked: true,
                    },
                    {
                        label: 'Paul’s “remain as called” rule includes Jewish covenantal identity',
                        topicSlug: 'what-does-remain-as-you-were-called-mean',
                        readingId: 'covenantal-location',
                        linked: true,
                    },
                ],
                relations: [
                    { type: 'contrasts with', target: 'not-under-mosaic-law', linked: true },
                    { type: 'qualifies', target: 'missionary-flexibility', linked: true },
                ],
            },
            {
                id: 'missionary-flexibility',
                title: 'Paul adapted practice for his mission',
                thesis: 'Paul retained Jewish identity but observed Torah practices flexibly rather than as a comprehensive obligation.',
                accent: 'amber',
                badges: ['Historical-critical', 'Moderate', 'Missional'],
                summary: 'On this reconstruction, Paul could circumcise Timothy, take a vow, or live “as a Jew” when mission required it, while also living as one outside the Law among Gentiles. Torah practices remained culturally and strategically meaningful, but Paul did not submit to the whole Law as an indivisible covenantal requirement.',
                contribution: 'This approach gives direct weight to Paul’s adaptive language in 1 Corinthians 9 and can account for varied behaviour across Acts and the letters without declaring either source historically worthless.',
                caution: 'Calling Paul’s practice merely strategic may understate the durability of his Jewish identity or imply that religious observance was insincere performance.',
                supporters: [
                    { name: 'Paul T. Sloan, describing a major scholarly position', sourceId: 'sloan-law-observance' },
                    { name: 'Heikki Räisänen', sourceId: 'sloan-law-observance' },
                ],
                predicates: [
                    {
                        label: 'Paul’s “as a Jew” language describes genuine adaptability',
                        topicSlug: 'was-pauls-mission-strategy-genuine-or-pragmatic',
                        readingId: 'genuine-flexibility',
                        linked: true,
                    },
                    {
                        label: 'Gentile mission removes Torah as an entry requirement',
                        topicSlug: 'why-did-table-fellowship-matter',
                        readingId: 'conversion-pressure',
                        linked: true,
                    },
                ],
                relations: [
                    { type: 'mediates between', target: 'torah-observant-jew', linked: true },
                    { type: 'mediates between', target: 'not-under-mosaic-law', linked: true },
                ],
            },
            {
                id: 'not-under-mosaic-law',
                title: 'Christians are no longer under the Mosaic Law',
                thesis: 'Paul remained ethnically Jewish but no longer regarded Torah as the covenant binding him after Christ.',
                accent: 'red',
                badges: ['Traditional Protestant', 'Conservative', 'Evangelical'],
                summary: 'This reading interprets “died to the Law,” “released from the Law,” and “not under the Law” as a real transfer of covenantal jurisdiction. Paul may continue selected Jewish customs for mission or conscience, but he does not do so because the Mosaic covenant remains binding. His governing norm is now the law of Christ in the Spirit.',
                contribution: 'It takes Paul’s strongest discontinuity language seriously and integrates his personal practice with a broader account of salvation history in which Christ fulfils the Mosaic covenant.',
                caution: 'Critics argue that it can turn continued Jewish practice into mere strategy and may not adequately explain Acts’ insistence that accusations of Paul teaching Jews to abandon Moses were false.',
                supporters: [
                    { name: 'Thomas R. Schreiner', sourceId: 'schreiner-law' },
                    { name: 'Douglas J. Moo', sourceId: 'moo-galatians' },
                ],
                predicates: [
                    {
                        label: 'The Mosaic covenant has been fulfilled and transformed in Christ',
                        topicSlug: 'how-does-the-old-covenant-relate-to-the-new',
                        readingId: 'fulfilled-and-transformed',
                        linked: true,
                    },
                    {
                        label: '“Works of the Law” includes the Mosaic Law as a covenantal whole',
                        topicSlug: 'what-does-paul-mean-by-works-of-the-law',
                        readingId: 'whole-torah',
                        linked: true,
                    },
                ],
                relations: [
                    { type: 'contrasts with', target: 'torah-observant-jew', linked: true },
                    { type: 'qualifies', target: 'missionary-flexibility', linked: true },
                ],
            },
        ],
        sources: [
            {
                id: 'sloan-law-observance',
                author: 'Paul T. Sloan',
                title: 'Paul’s Jewish practice remained important',
                detail: 'A peer-reviewed argument that Paul expected Jewish believers to remain Law-observant, engaging contrary readings in detail.',
                href: 'https://www.mdpi.com/2077-1444/16/1/91',
            },
            {
                id: 'paul-within-judaism',
                author: 'Mark D. Nanos and Magnus Zetterholm, editors',
                title: 'Paul Within Judaism',
                detail: 'A collection re-evaluating Paul’s Jewish identity, communities, and Gentile mission.',
                href: 'https://www.jstor.org/stable/j.ctt9m0vn7',
            },
            {
                id: 'acts-torah',
                author: 'Christoph W. Stenschke',
                title: 'Was Paul fully Torah observant according to Acts?',
                detail: 'A scholarly examination of what Luke’s narrative does and does not claim about Paul’s practice.',
                href: 'https://hts.org.za/index.php/hts/article/view/3396',
            },
            {
                id: 'schreiner-law',
                author: 'Thomas R. Schreiner',
                title: 'The Law and Its Fulfillment',
                detail: 'An evangelical account of continuity and discontinuity between Mosaic law and the law of Christ.',
                href: 'https://www.crossway.org/articles/10-things-you-should-know-about-the-biblical-covenants/',
            },
            {
                id: 'moo-galatians',
                author: 'Douglas J. Moo',
                title: 'Galatians',
                detail: 'An evangelical commentary on Paul’s relationship to the Law and the new covenant.',
                href: 'https://bakeracademic.com/p/galatians-douglas-j-moo/231029',
            },
        ],
        generation,
    },
];
